import React, { useCallback, useMemo, FC } from 'react'
import { useFormContext, useController } from 'react-hook-form'
import { MultiSelectCombobox } from '../../../../legacy-design-system/combobox'
import { JiraFormFieldTypes } from './jira-form-field-types'
import { FILTER_GROUPS, RULE_SEVERITY_LIST } from './legacy-constants'
import { useIntl } from 'react-intl'
import { isEmpty } from '../../../../util'
import { useRulesQuery } from '../api/use-rules-query'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { Rule } from '../api/shared'
import { JiraIntegrationForm } from './jira-form-schema'

export interface TicketRuleComboboxItem {
  name: string
  value: string
  description: string
  group: string
}

export interface TicketSeverityComboboxItem {
  name: string
  value: string
  translationId: string
  group: string
}

export const AUTO_CREATE_TICKET_RULES_TEST_ID = 'auto-create-ticket-rules'

export const mapRulesToComboboxItems = (
  rules: Rule[] = []
): TicketRuleComboboxItem[] =>
  rules.map((rule) => ({
    name: rule.title,
    value: rule.name,
    description: rule.description,
    group: FILTER_GROUPS.VULNERABILITY_RULES
  }))

export const mapRuleItemsToFormRules = (
  items: TicketRuleComboboxItem[] = []
): Rule[] => {
  return items.map((item) => ({
    title: item.name,
    name: item.value,
    description: item.description
  }))
}

export const mapSeveritiesToComboboxItems = (severities: string[] = []) => {
  return RULE_SEVERITY_LIST.filter((severity) =>
    severities.includes(severity.value)
  )
}

export const AutoCreateTicketsRules: FC = () => {
  const { formatMessage } = useIntl()
  const { watch } = useFormContext<JiraIntegrationForm>()
  const {
    field: { onChange: rulesOnChange, value: rulesValue }
  } = useController({
    name: JiraFormFieldTypes.AUTO_CREATE_TICKETS_RULES
  })

  const {
    field: { onChange: severityOnChange, value: severityValue }
  } = useController({
    name: JiraFormFieldTypes.AUTO_CREATE_TICKETS_SEVERITIES
  })

  const {
    state: { organizationUuid }
  } = useJiraIntegrationPage()

  const isAutoCreateTicketsEnabled = watch(
    JiraFormFieldTypes.AUTO_CREATE_TICKETS_ENABLED
  )

  const { rules: allRules } = useRulesQuery({ organizationUuid })

  const availableTicketRuleComboboxItems = useMemo(() => {
    return mapRulesToComboboxItems(allRules)
  }, [allRules])

  const handleChange = useCallback((newValues: TicketRuleComboboxItem[]) => {
    if (isEmpty(newValues)) {
      severityOnChange([])
      rulesOnChange([])
    } else {
      const newRules = newValues.filter(
        ({ group }) => group === FILTER_GROUPS.VULNERABILITY_RULES
      )
      rulesOnChange(mapRuleItemsToFormRules(newRules))

      const newSeverities = newValues.filter(
        ({ group }) => group === FILTER_GROUPS.SEVERITY
      )
      severityOnChange(newSeverities.map((value) => value.value))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isAutoCreateTicketsEnabled ? (
    <MultiSelectCombobox<TicketRuleComboboxItem | TicketSeverityComboboxItem>
      onChange={handleChange}
      items={[
        { items: RULE_SEVERITY_LIST, name: FILTER_GROUPS.SEVERITY },
        {
          items: availableTicketRuleComboboxItems,
          name: FILTER_GROUPS.VULNERABILITY_RULES
        }
      ]}
      placeHolderTag={formatMessage({ id: 'title_allRules' })}
      itemToID={({ group, name, value }) => `${group}_${name}_${value}`}
      itemToString={(item) =>
        'translationId' in item // Severity combobox item
          ? formatMessage({ id: item.translationId })
          : item.name
      }
      itemsEqual={(item1, item2) => item1.name === item2.name}
      values={
        [
          ...mapSeveritiesToComboboxItems(severityValue),
          ...mapRulesToComboboxItems(rulesValue)
        ] || []
      }
      data-testid={AUTO_CREATE_TICKET_RULES_TEST_ID}
    />
  ) : null
}
